import { useAtom } from 'jotai';
import { useCallback, useRef } from 'react';
import { useToastMessageState } from '../../../../../components/ToastMessage/ToastMessageProvider';
import { DEFAULT_LAYOUT, saveLayoutToPreferences, tearsheetV2LayoutAtom } from '../state';
import { TearsheetPanelType, TearsheetV2Layout } from '../types';
import {
  addItem,
  getUsedPanelTypes,
  moveAndResizeItem,
  moveItem,
  removeItem,
  resizeItem,
} from '../utils/layoutGrid';

const LAYOUT_BACKUP_KEY = 'tearsheet-v2-layout-backup';
const SAVE_DEBOUNCE_MS = 500;

function saveLayoutBackup(layout: TearsheetV2Layout): void {
  try {
    localStorage.setItem(LAYOUT_BACKUP_KEY, JSON.stringify(layout));
  } catch {
    // localStorage might be full or disabled - silently fail
  }
}

export function useLayoutActions() {
  const [layout, setLayout] = useAtom(tearsheetV2LayoutAtom);
  const { pushErrorToast } = useToastMessageState();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLayoutRef = useRef<TearsheetV2Layout | null>(null);

  const saveLayout = useCallback(
    (newLayout: TearsheetV2Layout) => {
      // Update local state immediately for responsive UI
      setLayout(newLayout);
      pendingLayoutRef.current = newLayout;

      // Debounce the API call to prevent excessive requests during rapid drag/resize
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(async () => {
        const layoutToSave = pendingLayoutRef.current;
        if (!layoutToSave) {
          return;
        }

        try {
          await saveLayoutToPreferences(layoutToSave);
        } catch (error) {
          console.error('Failed to save layout:', error);
          // Save to localStorage as backup for recovery
          saveLayoutBackup(layoutToSave);
          pushErrorToast({
            message:
              'Failed to save layout. Your changes have been saved locally and will persist until your next successful save.',
          });
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [setLayout, pushErrorToast]
  );

  const addPanel = useCallback(
    (panelType: TearsheetPanelType, position?: { x: number; y: number }, size?: { w: number; h: number }) => {
      const newLayout = addItem(layout, panelType, position, size);
      if (newLayout === layout) {
        // Item wasn't added (no space or invalid placement)
        pushErrorToast({ message: 'No space available for this panel.' });
        return;
      }
      saveLayout(newLayout);
    },
    [layout, saveLayout, pushErrorToast]
  );

  const removePanel = useCallback(
    (itemId: string) => {
      const newLayout = removeItem(layout, itemId);
      saveLayout(newLayout);
    },
    [layout, saveLayout]
  );

  const resizePanel = useCallback(
    (itemId: string, newW: number, newH: number) => {
      const newLayout = resizeItem(layout, itemId, newW, newH);
      if (newLayout === layout) {
        // Resize failed (collision or out of bounds)
        return;
      }
      saveLayout(newLayout);
    },
    [layout, saveLayout]
  );

  const movePanel = useCallback(
    (itemId: string, newX: number, newY: number) => {
      const newLayout = moveItem(layout, itemId, newX, newY);
      if (newLayout === layout) {
        // Move failed (collision or out of bounds)
        return;
      }
      saveLayout(newLayout);
    },
    [layout, saveLayout]
  );

  const moveAndResizePanel = useCallback(
    (itemId: string, newX: number, newY: number, newW: number, newH: number) => {
      const newLayout = moveAndResizeItem(layout, itemId, newX, newY, newW, newH);
      if (newLayout === layout) {
        // Move/resize failed (collision or out of bounds)
        return;
      }
      saveLayout(newLayout);
    },
    [layout, saveLayout]
  );

  const resetLayout = useCallback(() => {
    saveLayout(DEFAULT_LAYOUT);
  }, [saveLayout]);

  return {
    layout,
    usedPanels: getUsedPanelTypes(layout),
    addPanel,
    removePanel,
    resizePanel,
    movePanel,
    moveAndResizePanel,
    resetLayout,
  };
}
